import { Armchair, Circle, Square, Users } from 'lucide-react';
import clsx from 'clsx';
import { to12h } from '@/services/appointments.service';
import type { FloorTableCard } from '@/services/tables.service';

const SHAPE_ICON = { square: Square, round: Circle, booth: Armchair } as const;
const SHAPE_RADIUS = { square: 'rounded-xl', round: 'rounded-3xl', booth: 'rounded-xl rounded-l-[28px]' } as const;

const STATE_STYLE: Record<FloorTableCard['state'], string> = {
  free: 'border-success/30 bg-success-soft/60 hover:border-success/50',
  reserved_soon: 'border-warning/40 bg-warning-soft hover:border-warning/60',
  seated: 'border-primary/50 bg-primary-soft hover:border-primary',
  no_show: 'border-border bg-surface-alt text-text-muted hover:border-border-strong',
};

const STATE_DOT: Record<FloorTableCard['state'], string> = {
  free: 'bg-success',
  reserved_soon: 'bg-warning',
  seated: 'bg-primary',
  no_show: 'bg-text-muted',
};

const STATE_LABEL: Record<FloorTableCard['state'], string> = {
  free: 'Free',
  reserved_soon: 'Reserved soon',
  seated: 'Seated',
  no_show: 'No-show',
};

/** Card min-width scales with seats so a 2-top and a 10-top read as different sizes on the floor. */
function sizeClass(seats: number): string {
  if (seats <= 2) return 'w-[132px]';
  if (seats <= 4) return 'w-[152px]';
  if (seats <= 8) return 'w-[172px]';
  return 'w-[196px]';
}

interface Props {
  card: FloorTableCard;
  onClick?: () => void;
}

export function TableCard({ card, onClick }: Props) {
  const ShapeIcon = SHAPE_ICON[card.shape];
  const b = card.booking;

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'flex flex-col gap-2 border p-3 text-left shadow-soft transition-all duration-150 hover:-translate-y-0.5 hover:shadow-elevated',
        SHAPE_RADIUS[card.shape],
        STATE_STYLE[card.state],
        sizeClass(card.seats),
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <ShapeIcon size={14} className="shrink-0 opacity-70" />
          <span className="truncate font-display text-sm font-semibold text-text-primary">{card.name}</span>
        </div>
        <span className={clsx('mt-0.5 h-2 w-2 shrink-0 rounded-full', STATE_DOT[card.state])} aria-hidden />
      </div>

      <div className="flex items-center gap-1 text-2xs text-text-muted">
        <Users size={12} /> {card.seats} seats
      </div>

      {b ? (
        <div className="rounded-lg bg-surface/70 px-2 py-1.5 text-2xs leading-snug">
          <div className="truncate font-medium text-text-primary">{b.customerName ?? 'Guest'}{b.partySize ? ` · ${b.partySize}` : ''}</div>
          <div className="text-text-muted">{to12h(b.slotStart)}–{to12h(b.slotEnd)}</div>
        </div>
      ) : (
        <div className="text-2xs text-text-muted">{STATE_LABEL[card.state]}</div>
      )}
    </button>
  );
}
